import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PublicHeader, Breadcrumbs, SeoHead } from "@/components/PublicSeo";
import { SeoFooter } from "@/components/SeoFooter";
import NotFound from "@/pages/NotFound";

function LinkGrid({ title, items, testid }) {
  if (!items.length) return null;
  return (
    <section className="mb-10" data-testid={testid}>
      <h2 className="font-heading font-bold text-lg mb-3">{title}</h2>
      <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((p, i) => (
          <li key={p.path} className="rounded-xl border border-border bg-surface px-4 py-3 flex items-center justify-between gap-3">
            <Link to={p.path} className="font-medium hover:text-brand-pink"><span className="text-muted-foreground mr-2">{i + 1}.</span>{p.label}</Link>
            <span className="text-xs text-muted-foreground shrink-0">{p.count} profile{p.count === 1 ? "" : "s"}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function RegionHub() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/seo/region/${slug}`).then((r) => setD(r.data)).catch(() => setD({ status: "not_found" }));
  }, [slug]);
  if (!d) return <div className="min-h-screen bg-background"><PublicHeader /><p className="p-10 text-muted-foreground">Loading…</p></div>;
  if (d.status === "not_found") return <NotFound />;
  return (
    <div className="min-h-screen bg-background" data-testid="region-hub-page">
      <SeoHead title={d.title} description={d.description} canonical={d.canonical_url} robots={d.robots} />
      <PublicHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <Breadcrumbs items={[{ label: "Home", path: "/" }, { label: "Influencers", path: "/influencer-directory" }, { label: d.region, path: `/regions/${d.slug}` }]} />
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mb-4" data-testid="region-h1">{d.h1}</h1>
        <p className="text-muted-foreground max-w-3xl mb-10" data-testid="region-intro">{d.intro}</p>
        <LinkGrid title="Country pages" items={d.countries} testid="region-countries" />
        <LinkGrid title={`Top city pages in ${d.region}`} items={d.cities} testid="region-cities" />
        {!d.indexable && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center mb-10" data-testid="region-empty">
            <p className="font-semibold mb-2">No city or country pages in {d.region} have enough creators yet.</p>
            <p className="text-sm text-muted-foreground">Creators from {d.region} can join for free to be listed.</p>
          </div>
        )}
        <section className="mb-10" data-testid="region-others">
          <h2 className="font-heading font-bold text-lg mb-3">Other regions</h2>
          <ul className="flex flex-wrap gap-2">{d.other_regions.map((r) => <li key={r.path}><Link to={r.path} className="inline-block rounded-full border border-border px-3 py-1.5 text-sm hover:border-brand-pink">{r.label}</Link></li>)}</ul>
        </section>
        <div className="rounded-3xl bg-brand-gradient text-white p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="font-heading font-bold text-xl">Run a campaign with creators in {d.region}</p>
          <div className="flex gap-3">
            <Link to="/?join=brand" data-testid="region-cta-brand" className="rounded-full bg-white text-foreground px-5 py-2.5 font-semibold text-sm">Register as a brand</Link>
            <Link to="/?join=creator" data-testid="region-cta-creator" className="rounded-full border border-white/70 px-5 py-2.5 font-semibold text-sm">Join as a creator</Link>
          </div>
        </div>
      </main>
      <SeoFooter />
    </div>
  );
}
